import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, Home, AlertTriangle } from 'lucide-react';
import { errorHandler } from '@/lib/errorHandler';
import ErrorBoundary from './ErrorBoundary';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('🚨 GlobalErrorBoundary caught an error:', error);
    console.error('Error Info:', errorInfo);
    
    // Process error through our error handling system
    const appError = errorHandler.handleUnexpectedError(error, 'Global Error Boundary', {
      showNotification: false, // Don't show notification as we're rendering our own UI
      logToConsole: true,
      logToService: true
    });
    
    this.setState({
      error,
      errorInfo,
      errorId: appError.id
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Use our enhanced ErrorBoundary component
      return (
        <ErrorBoundary
          context="Global Application Error"
          enableRetry={true}
          enableReporting={true}
          maxRetries={1}
          onError={(error, errorInfo) => {
            console.error('Enhanced Error Boundary:', error, errorInfo);
          }}
        >
          {/* This will trigger the error boundary's error UI since we have an error */}
          <div>{(() => { throw this.state.error })()}</div>
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary
        context="Global Application"
        enableRetry={true}
        enableReporting={true}
        maxRetries={3}
        onError={(error, errorInfo) => {
          console.error('Global Error Boundary:', error, errorInfo);
          errorHandler.handleUnexpectedError(error, 'Global Error Boundary', {
            showNotification: false,
            logToConsole: true,
            logToService: true
          });
        }}
      >
        {this.props.children}
      </ErrorBoundary>
    );
  }
}
